/**
 * Plan a pack run (F7.1, POST /pack-runs) and start it (F7.2, POST /pack-runs/:id/start).
 * uc-core CreatePackRunDto / StartPackRunDto: kilos as "270.000" (≤ 3 decimals); plannedDate
 * YYYY-MM-DD; the scale check in grams (numbers); leftoverKg "0.000" when nothing was left.
 */
import { parseKgToGrams } from '@/lib/weight'
import { gramsToKgString } from '../../api/mappers'

// ---- plan -------------------------------------------------------------------

export interface PlanDraft {
  skuId: string
  batchUnitId: string
  plannedKg: string
  locationId: string
  equipmentId: string
  operatorId: string
  plannedDate: string
}
export type PlanProblems = Partial<Record<keyof PlanDraft, string>>

export const emptyPlanDraft = (today: string): PlanDraft => ({
  skuId: '',
  batchUnitId: '',
  plannedKg: '',
  locationId: '',
  equipmentId: '',
  operatorId: '',
  plannedDate: today,
})

/** Kilos × 1000 ÷ pack size, rounded down (as the server works it out). */
export function plannedPouches(grams: number | null, netG: number | null | undefined): number | null {
  if (grams === null || !netG || netG <= 0 || grams <= 0) return null
  return Math.floor(grams / netG)
}

/** Typed kilos → grams (exact); null when unreadable or negative. */
export function kgTextToGrams(text: string): number | null {
  const t = text.trim()
  if (!t) return null
  const g = parseKgToGrams(t)
  return g === null || g < 0 ? null : g
}

export function validatePlanDraft(
  d: PlanDraft,
  ctx: { availableG: number | null; netG: number | null; today: string },
): PlanProblems {
  const p: PlanProblems = {}
  if (!d.skuId) p.skuId = 'Pick the product'
  if (!d.batchUnitId) p.batchUnitId = 'Pick a batch'
  const g = kgTextToGrams(d.plannedKg)
  if (!d.plannedKg.trim()) p.plannedKg = 'Enter the kilos to pack'
  else if (g === null) p.plannedKg = 'Kilos with at most 3 decimals, e.g. 270.500'
  else if (g === 0) p.plannedKg = 'Must be above 0 kg'
  else if (ctx.availableG !== null && g > ctx.availableG) p.plannedKg = `Only ${gramsToKgString(ctx.availableG)} kg is available`
  else if (ctx.netG && g < ctx.netG) p.plannedKg = 'Not enough for one pouch'
  if (!d.locationId) p.locationId = 'Pick the packing hall'
  if (!d.equipmentId) p.equipmentId = 'Pick the pack line'
  if (!d.operatorId) p.operatorId = 'Pick the pack operator'
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.plannedDate)) p.plannedDate = 'Pick the day'
  else if (d.plannedDate < ctx.today) p.plannedDate = 'Pick today or a later day'
  return p
}

export const planOk = (p: PlanProblems) => Object.values(p).every((v) => !v)

/** POST body. Call only when valid. */
export function planBody(d: PlanDraft) {
  return {
    skuId: d.skuId,
    batchUnitId: d.batchUnitId,
    plannedKg: gramsToKgString(kgTextToGrams(d.plannedKg) ?? 0),
    locationId: d.locationId,
    equipmentId: d.equipmentId,
    operatorId: d.operatorId,
    plannedDate: d.plannedDate,
  }
}

// ---- start ------------------------------------------------------------------

export interface StartDraft {
  cleanoutDone: boolean
  cleanoutPhotoId: string
  allergenCleanConfirmed: boolean
  leftoverKg: string
  scaleId: string
  referenceG: string
  readingG: string
  labelVersionId: string
  labelChecked: boolean
}
export type StartProblems = Partial<Record<keyof StartDraft, string>>

export const emptyStartDraft = (p: Partial<StartDraft> = {}): StartDraft => ({
  cleanoutDone: false,
  cleanoutPhotoId: '',
  allergenCleanConfirmed: false,
  leftoverKg: '0',
  scaleId: '',
  referenceG: '500',
  readingG: '',
  labelVersionId: '',
  labelChecked: false,
  ...p,
})

const GRAMS = /^\d{1,6}(\.\d{1,2})?$/

/** Reading off by more than the tolerance (grams); null when either is unreadable. */
export function scaleOff(referenceG: string, readingG: string, toleranceG: number): boolean | null {
  if (!GRAMS.test(referenceG.trim()) || !GRAMS.test(readingG.trim())) return null
  return Math.abs(Number(readingG) - Number(referenceG)) > toleranceG
}

export function validateStartDraft(d: StartDraft): StartProblems {
  const p: StartProblems = {}
  if (!d.cleanoutDone) p.cleanoutDone = 'Confirm the line and hopper are empty and clean'
  if (!d.cleanoutPhotoId) p.cleanoutPhotoId = 'Add a photo of the empty hopper'
  const left = d.leftoverKg.trim()
  if (!left) p.leftoverKg = 'Type 0 if nothing was left'
  else if (kgTextToGrams(left) === null) p.leftoverKg = 'Kilos with at most 3 decimals'
  if (!d.scaleId) p.scaleId = 'Pick the scale used for the check'
  if (!GRAMS.test(d.referenceG.trim()) || Number(d.referenceG) < 1) p.referenceG = 'The check weight in grams, e.g. 500'
  if (!d.readingG.trim()) p.readingG = 'Type what the scale shows'
  else if (!GRAMS.test(d.readingG.trim())) p.readingG = 'Grams, e.g. 500.5'
  if (!d.labelVersionId) p.labelVersionId = 'Pick the label version on the roll'
  else if (!d.labelChecked) p.labelChecked = 'Check the roll matches the label lines'
  return p
}

export const startOk = (p: StartProblems) => Object.values(p).every((v) => !v)

/** POST body. Call only when valid. */
export function startBody(d: StartDraft) {
  return {
    cleanoutPhotoId: d.cleanoutPhotoId,
    allergenCleanConfirmed: d.allergenCleanConfirmed,
    leftoverKg: gramsToKgString(kgTextToGrams(d.leftoverKg) ?? 0),
    scaleCheck: { equipmentId: d.scaleId, referenceG: Number(d.referenceG.trim()), readingG: Number(d.readingG.trim()) },
    labelVersionId: d.labelVersionId,
  }
}
